import { useEffect, useState } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'

/**
 * Mount / fade lifecycle for a full-screen overlay, plus the page scroll lock.
 *
 * Opening mounts first and flips `isVisible` two frames later, so the CSS
 * transition has a committed start state to animate from. Closing flips
 * `isVisible` first and unmounts once the transition has had time to finish.
 */
export const useOverlayTransition = (
  isOpen: boolean,
  transitionDuration: number
) => {
  const { openModal, closeModal } = useModalScroll()
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      openModal()
      setIsMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true))
      })
      return
    }
    setIsVisible(false)
    const timer = setTimeout(() => {
      setIsMounted(false)
      closeModal()
    }, transitionDuration)
    return () => clearTimeout(timer)
  }, [isOpen, transitionDuration, openModal, closeModal])

  return { isMounted, isVisible }
}
